import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, Wrench, CheckCircle, Clock, Shield, User, FileText, MessageSquare } from 'lucide-react';
import { TimeFilter, PeriodType } from './TimeFilter';
import { ProductBreakdownTable } from './UserBreakdownTable';
import { PageHeader } from '../common/ui';
import { getUserPreference, setUserPreference } from '../../hooks/useUserPreferences';

interface UserStat {
  user_name: string;
  user_id: number | null;
  repairs_created: number;
  devices_repaired: number;
  repairs_completed: number;
  repairs_clarified: number;
  total: number;
}

interface ModuleRepairCounts {
  paid: number;
  free_of_charge: number;
  warranty: number;
  unknown: number;
}

interface ModuleStat {
  module_name: string;
  repaired: ModuleRepairCounts;
  replaced: ModuleRepairCounts;
}

interface ProductModuleStats {
  product_name: string;
  product_id: number | null;
  modules: ModuleStat[];
}

interface RepairStats {
  period: {
    start: string;
    end: string;
  };
  created: number;
  completed: number;
  warranty_total: number;
  non_warranty_total: number;
  module_stats: ProductModuleStats[];
  user_stats: UserStat[];
  totals: {
    repairs_created: number;
    devices_repaired: number;
    repairs_completed: number;
    repairs_clarified: number;
  };
  repaired_by_product: Array<{
    product_name: string;
    product_id: number | null;
    count: number;
  }>;
  warranty_by_product: Array<{
    product_name: string;
    product_id: number | null;
    warranty: number;
    non_warranty: number;
  }>;
}

const RepairStats: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [period, setPeriod] = useState<PeriodType>(() => {
    const p = searchParams.get('period');
    if (p && ['7d', '30d', '365d', 'this_month', 'this_year', 'custom'].includes(p)) {
      return p as PeriodType;
    }
    if (searchParams.get('start') && searchParams.get('end')) {
      return 'custom';
    }
    const saved = getUserPreference('repairStats', 'period', '30d');
    if (['7d', '30d', '365d', 'this_month', 'this_year'].includes(saved)) {
      return saved as PeriodType;
    }
    return '30d';
  });
  
  const [customStart, setCustomStart] = useState(() => searchParams.get('start') || '');
  const [customEnd, setCustomEnd] = useState(() => searchParams.get('end') || '');
  
  const [stats, setStats] = useState<RepairStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError('');
      
      let url = `/api/stats/repairs?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url = `/api/stats/repairs?start=${customStart}&end=${customEnd}`;
      }
      
      const response = await axios.get(url);
      setStats(response.data);
    } catch (err: any) {
      console.error('Error fetching repair stats:', err);
      setError(err.response?.data?.error || 'Failed to fetch repair statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [period, customStart, customEnd]);

  const handlePeriodChange = (newPeriod: PeriodType, start?: string, end?: string) => {
    setPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setUserPreference('repairStats', 'period', newPeriod);
    }
    
    const params = new URLSearchParams();
    if (newPeriod === 'custom' && start && end) {
      params.set('start', start);
      params.set('end', end);
      setCustomStart(start);
      setCustomEnd(end);
    } else {
      params.set('period', newPeriod);
      setCustomStart('');
      setCustomEnd('');
    }
    setSearchParams(params);
  };

  const SkeletonTable = () => (
    <div className="bg-white rounded-lg shadow animate-pulse">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="h-6 bg-gray-200 rounded w-48"></div>
      </div>
      <div className="p-6 space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-4">
            <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
            <div className="flex-1 h-4 bg-gray-200 rounded"></div>
            <div className="w-12 h-4 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 pb-20">
      <PageHeader
        title="Repair Statistics"
        subtitle="Detailed breakdown of repair activity by user"
        onBack={() => window.history.back()}
      />

      <TimeFilter
        period={period}
        customStart={customStart}
        customEnd={customEnd}
        onChange={handlePeriodChange}
      />

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-6">
          <SkeletonTable />
          <SkeletonTable />
          <SkeletonTable />
        </div>
      ) : stats ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.totals.repairs_created}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Clarified</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.totals.repairs_clarified}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Devices Repaired</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.totals.devices_repaired}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{stats.totals.repairs_completed}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Module Repair/Replacement Statistics */}
          {stats.module_stats && stats.module_stats.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Parts Repaired & Replaced</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Module repair and replacement statistics by product type
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th rowSpan={2} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                        Module
                      </th>
                      <th colSpan={4} className="px-4 py-2 text-center text-xs font-medium text-purple-700 uppercase tracking-wider border-r border-gray-200 bg-purple-50">
                        Repaired
                      </th>
                      <th colSpan={4} className="px-4 py-2 text-center text-xs font-medium text-blue-700 uppercase tracking-wider bg-blue-50">
                        Replaced
                      </th>
                    </tr>
                    <tr>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50/50">
                        Paid
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50/50">
                        Free
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50/50">
                        Warranty
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 bg-purple-50/50">
                        N/A
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50">
                        Paid
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50">
                        Free
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50">
                        Warranty
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50/50">
                        N/A
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.module_stats.map((product, productIndex) => (
                      <React.Fragment key={product.product_id || productIndex}>
                        {/* Product header row */}
                        <tr className="bg-gray-100">
                          <td colSpan={9} className="px-4 py-2 text-sm font-semibold text-gray-900">
                            {product.product_name}
                          </td>
                        </tr>
                        {/* Module rows */}
                        {product.modules.map((module, moduleIndex) => (
                            <tr key={moduleIndex} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200 pl-8">
                                {module.module_name}
                              </td>
                              <td className={`px-3 py-3 text-center text-sm ${module.repaired.paid > 0 ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                                {module.repaired.paid || '-'}
                              </td>
                              <td className={`px-3 py-3 text-center text-sm ${module.repaired.free_of_charge > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                                {module.repaired.free_of_charge || '-'}
                              </td>
                              <td className={`px-3 py-3 text-center text-sm ${module.repaired.warranty > 0 ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                                {module.repaired.warranty || '-'}
                              </td>
                              <td className={`px-3 py-3 text-center text-sm border-r border-gray-200 ${module.repaired.unknown > 0 ? 'text-gray-500 font-medium' : 'text-gray-400'}`}>
                                {module.repaired.unknown || '-'}
                              </td>
                              <td className={`px-3 py-3 text-center text-sm ${module.replaced.paid > 0 ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                                {module.replaced.paid || '-'}
                              </td>
                              <td className={`px-3 py-3 text-center text-sm ${module.replaced.free_of_charge > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                                {module.replaced.free_of_charge || '-'}
                              </td>
                              <td className={`px-3 py-3 text-center text-sm ${module.replaced.warranty > 0 ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                                {module.replaced.warranty || '-'}
                              </td>
                              <td className={`px-3 py-3 text-center text-sm ${module.replaced.unknown > 0 ? 'text-gray-500 font-medium' : 'text-gray-400'}`}>
                                {module.replaced.unknown || '-'}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* User Activity Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Repair Activity by User</h3>
              <p className="text-sm text-gray-500 mt-1">
                Key repair workflow actions performed by each team member
              </p>
            </div>
            
            {stats.user_stats.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No repair activity in this period
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center justify-end gap-1">
                          <FileText className="w-3.5 h-3.5" />
                          <span>Created</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center justify-end gap-1">
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span>Clarified</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center justify-end gap-1">
                          <Wrench className="w-3.5 h-3.5" />
                          <span>Repaired</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center justify-end gap-1">
                          <CheckCircle className="w-3.5 h-3.5" />
                          <span>Completed</span>
                        </div>
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.user_stats.map((user, index) => (
                      <tr key={user.user_id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <User className="h-4 w-4 text-gray-500" />
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {user.user_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className={`text-sm font-medium ${user.repairs_created > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {user.repairs_created || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className={`text-sm font-medium ${user.repairs_clarified > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                            {user.repairs_clarified || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className={`text-sm font-medium ${user.devices_repaired > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                            {user.devices_repaired || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className={`text-sm font-medium ${user.repairs_completed > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                            {user.repairs_completed || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-semibold text-gray-900">
                            {user.total}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-900">Total</span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-blue-600">{stats.totals.repairs_created}</span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-amber-600">{stats.totals.repairs_clarified}</span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-purple-600">{stats.totals.devices_repaired}</span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-green-600">{stats.totals.repairs_completed}</span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right">
                        <span className="text-sm font-semibold text-gray-900">
                          {stats.totals.repairs_created + stats.totals.devices_repaired + stats.totals.repairs_completed + stats.totals.repairs_clarified}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">What each column means:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-700">Created</span>
                  <p className="text-gray-500">New repair tickets created</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-700">Clarified</span>
                  <p className="text-gray-500">Clarification Required / Dev Input / Customer Response → Any other</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Wrench className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-700">Repaired</span>
                  <p className="text-gray-500">Work in Progress → Repaired</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-700">Completed</span>
                  <p className="text-gray-500">Any status → Completed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Warranty Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Warranty Repairs</h3>
                  <p className="text-2xl font-bold text-amber-600">{stats.warranty_total}</p>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {stats.warranty_total + stats.non_warranty_total > 0
                  ? `${Math.round((stats.warranty_total / (stats.warranty_total + stats.non_warranty_total)) * 100)}% of completed repairs`
                  : 'No completed repairs'}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gray-500 rounded-lg flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Non-Warranty Repairs</h3>
                  <p className="text-2xl font-bold text-gray-600">{stats.non_warranty_total}</p>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {stats.warranty_total + stats.non_warranty_total > 0
                  ? `${Math.round((stats.non_warranty_total / (stats.warranty_total + stats.non_warranty_total)) * 100)}% of completed repairs`
                  : 'No completed repairs'}
              </div>
            </div>
          </div>

          {/* Devices Repaired by Product */}
          <ProductBreakdownTable
            title="Devices Repaired by Product Type"
            data={stats.repaired_by_product}
            columns={[{ key: 'count', label: 'Repairs Completed' }]}
            emptyMessage="No repairs were completed in this period"
          />

          {/* Warranty vs Non-Warranty by Product */}
          {stats.warranty_by_product.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Warranty vs Non-Warranty by Product</h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Warranty
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Non-Warranty
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Warranty %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.warranty_by_product.map((row, index) => {
                      const total = row.warranty + row.non_warranty;
                      const warrantyPercent = total > 0 ? Math.round((row.warranty / total) * 100) : 0;
                      
                      return (
                        <tr key={row.product_id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {row.product_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-amber-600 font-medium">
                            {row.warranty}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600">
                            {row.non_warranty}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                            {total}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-amber-500 h-2 rounded-full"
                                  style={{ width: `${warrantyPercent}%` }}
                                />
                              </div>
                              <span className="text-sm text-gray-600 w-10">{warrantyPercent}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-6 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                        Total
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-amber-600">
                        {stats.warranty_total}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-600">
                        {stats.non_warranty_total}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                        {stats.warranty_total + stats.non_warranty_total}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-600">
                        {stats.warranty_total + stats.non_warranty_total > 0
                          ? `${Math.round((stats.warranty_total / (stats.warranty_total + stats.non_warranty_total)) * 100)}%`
                          : '0%'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default RepairStats;
